/**
 * SocialPulseProfile — /@:handle
 *
 * The SocialPulse-branded "My Pulse" profile page. Layout: left rail
 * (identity + sections + network sites) · center feed · right rail
 * (Social Capital · Top Badges · My Links · Marketplace · Events).
 *
 * Data comes from `GET /api/member/{handle}` — the backend already
 * assembles the full public/owner-scoped payload (identity, follower
 * counts, social capital summary, links, marketplace items, upcoming
 * events). Every "Coming soon" sidebar tab is intentional: we route
 * visitors into specialist SocialPulse Network sites instead of
 * duplicating those flows here.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Activity, Home, Search, Bell, MessageSquare, Plus, ChevronDown, MapPin,
  BadgeCheck, Copy, ExternalLink, Loader2, Camera, FileText, Briefcase,
  Layers, ShoppingBag, Wrench, Calendar, Users, UserPlus, Heart,
  Bookmark, LineChart, Trees, Utensils, GraduationCap, Image as ImageIcon,
  Store, Globe2, Youtube, Instagram, Linkedin, Twitter, Facebook, Link2,
  Award, Camera as CameraIcon, PlusCircle, Info,
} from "lucide-react";
import axios from "axios";
import { API, http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const LEFT_TABS = [
  { key: "pulse",       label: "My Pulse",      icon: Activity, active: true },
  { key: "about",       label: "About",         icon: Info },
  { key: "photos",      label: "Photos",        icon: ImageIcon },
  { key: "articles",    label: "Articles",      icon: FileText },
  { key: "resume",      label: "Resume",        icon: Briefcase },
  { key: "portfolio",   label: "Portfolio",     icon: Layers },
  { key: "marketplace", label: "Marketplace",   icon: ShoppingBag, badgeKey: "marketplace" },
  { key: "services",    label: "Services",      icon: Wrench,      badgeKey: "services" },
  { key: "events",      label: "Events",        icon: Calendar },
  { key: "groups",      label: "Groups",        icon: Users },
  { key: "connections", label: "Connections",   icon: UserPlus },
  { key: "causes",      label: "Causes",        icon: Heart },
  { key: "saved",       label: "Saved",         icon: Bookmark },
  { key: "analytics",   label: "Analytics",     icon: LineChart, ownerOnly: true },
];

const NETWORK_SITES = [
  { label: "NaturePulse",  sub: "Observe Nature",     icon: Trees,       href: "https://naturepulse.pro" },
  { label: "FarmersPulse", sub: "Smart Farming",      icon: Wrench,      href: "https://farmerspulse.pro" },
  { label: "FoodPulse",    sub: "Reduce Food Waste",  icon: Utensils,    href: "https://foodpulse.net" },
  { label: "AlumniPulse",  sub: "Stay Connected",     icon: GraduationCap,href: "https://alumnipulse.pro" },
  { label: "PhotoPulse",   sub: "Share Moments",      icon: CameraIcon,  href: "https://photopulse.pro" },
  { label: "TravelerPulse",sub: "Explore Places",     icon: MapPin,      href: "https://travelerpulse.pro" },
  { label: "SellnBuy",     sub: "Buy, Sell, Donate",  icon: Store,       href: "https://sellnbuy.pro" },
];

const LINK_ICONS = {
  globe: Globe2, youtube: Youtube, instagram: Instagram,
  linkedin: Linkedin, twitter: Twitter, facebook: Facebook, link: Link2,
};

const BADGES = [
  { key: "nature",      label: "Nature Observer",   icon: Trees,        tint: "emerald" },
  { key: "food",        label: "Food Rescuer",      icon: Utensils,     tint: "orange" },
  { key: "community",   label: "Community Helper",  icon: Users,        tint: "violet" },
  { key: "knowledge",   label: "Knowledge Sharer",  icon: GraduationCap,tint: "amber" },
];

const TINTS = {
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  orange:  "bg-orange-50 text-orange-700 ring-orange-200",
  violet:  "bg-violet-50 text-violet-700 ring-violet-200",
  amber:   "bg-amber-50 text-amber-700 ring-amber-200",
  blue:    "bg-blue-50 text-blue-700 ring-blue-200",
  rose:    "bg-rose-50 text-rose-700 ring-rose-200",
};

export default function SocialPulseProfile() {
  const { handle } = useParams();
  const { user: currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("pulse");

  const cleanHandle = (handle || "").replace(/^@/, "").toLowerCase();

  useEffect(() => {
    document.title = data?.identity?.name
      ? `${data.identity.name} · SocialPulse`
      : `@${cleanHandle} · SocialPulse`;
    setMeta("description", data?.identity?.bio || `SocialPulse profile of @${cleanHandle}.`);
    setLink("canonical", `https://socialpulse.com/@${cleanHandle}`);
  }, [data, cleanHandle]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/member/${cleanHandle}`, { timeout: 8000 })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Profile not found."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cleanHandle]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500 text-sm bg-slate-50">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen grid place-items-center bg-white px-6" data-testid="sp-profile-error">
        <div className="max-w-md text-center">
          <h1 className="font-serif font-bold text-slate-900 text-2xl">Profile not found</h1>
          <p className="mt-2 text-slate-600 text-sm">
            @{cleanHandle} isn't on SocialPulse yet, or it has been removed.
          </p>
          <Link to="/" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-violet-700 hover:text-violet-900">
            Back home
          </Link>
        </div>
      </div>
    );
  }

  const id = data.identity || {};
  const s = data.stats || {};
  const stats = { followers: s.followers || 0, following: s.following || 0, posts: s.posts || 0, socialCap: data.social_capital?.total_points || 0 };
  const isOwner = data.is_owner || (!!currentUser && (currentUser.id === id.id || (currentUser.handle || "").toLowerCase() === cleanHandle));
  const shareUrl = `socialpulse.com/pulse/${cleanHandle}`;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sp-profile-page">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="w-8 h-8 grid place-items-center rounded-lg bg-gradient-to-br from-emerald-500 to-violet-600 text-white">
              <Activity className="w-4 h-4" strokeWidth={2.5} />
            </span>
            <span className="font-serif font-bold text-slate-900 text-base sm:text-lg tracking-tight hidden sm:inline">
              SocialPulse
            </span>
          </Link>
          <label className="relative flex-1 max-w-md hidden md:block">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              placeholder="Search people, topics, places…"
              className="w-full h-9 pl-9 pr-3 rounded-full bg-slate-100 focus:bg-white focus:ring-2 focus:ring-violet-500 focus:outline-none text-sm text-slate-800 placeholder:text-slate-400"
            />
          </label>
          <div className="ml-auto flex items-center gap-1 sm:gap-2">
            <IconBtn icon={Home} label="Home" href="/" />
            <IconBtn icon={Bell} label="Notifications" />
            <IconBtn icon={MessageSquare} label="Messages" />
            <IconBtn icon={Plus} label="Create" tint />
            <button className="flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full hover:bg-slate-100">
              <span className="w-8 h-8 rounded-full ring-2 ring-white overflow-hidden bg-slate-200">
                {id.avatar_url && <img src={id.avatar_url} alt="" className="w-full h-full object-cover" />}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-500 hidden sm:block" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 lg:px-6 py-5 grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)_320px] gap-5">

        {/* ── Left rail ── */}
        <aside className="hidden lg:block" data-testid="sp-profile-left">
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-3">
            <div className="flex items-center gap-2.5 p-1">
              <span className="w-9 h-9 rounded-full overflow-hidden bg-slate-200 shrink-0">
                {id.avatar_url && <img src={id.avatar_url} alt="" className="w-full h-full object-cover" />}
              </span>
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 text-sm truncate">{id.name || id.handle}</p>
                <p className="text-[11px] text-slate-500 truncate">@{id.handle}</p>
              </div>
            </div>
            <ul className="mt-3 space-y-0.5">
              {LEFT_TABS.filter((t) => !t.ownerOnly || isOwner).map((t) => (
                <SidebarItem
                  key={t.key}
                  {...t}
                  active={activeTab === t.key}
                  onClick={() => setActiveTab(t.key)}
                  badge={t.badgeKey ? countBadge(data, t.badgeKey) : undefined}
                />
              ))}
            </ul>
          </div>

          <div className="mt-4 rounded-2xl bg-white ring-1 ring-slate-200 p-3">
            <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-[.15em] text-slate-400">
              My Network
            </p>
            <ul className="mt-1 space-y-0.5">
              {NETWORK_SITES.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`sp-profile-net-${s.label.toLowerCase()}`}
                    className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-slate-50 transition-colors"
                  >
                    <span className="w-8 h-8 grid place-items-center rounded-full bg-emerald-50 text-emerald-700">
                      <s.icon className="w-4 h-4" strokeWidth={2.2} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{s.label}</p>
                      <p className="text-[10.5px] text-slate-500 truncate">{s.sub}</p>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
            <Link
              to="/network"
              data-testid="sp-profile-explore-more"
              className="mt-2 flex items-center justify-center h-9 rounded-full ring-1 ring-slate-200 text-sm font-semibold text-slate-600 hover:text-violet-700 hover:ring-violet-300"
            >
              Explore More
            </Link>
          </div>
        </aside>

        {/* ── Center column ── */}
        <main className="min-w-0">
          {/* Header card */}
          <section className="rounded-3xl bg-white ring-1 ring-slate-200 overflow-hidden" data-testid="sp-profile-header">
            <div className="h-40 sm:h-52 relative" style={{
              background: id.cover_url
                ? `url(${id.cover_url}) center/cover`
                : "linear-gradient(135deg,#A7F3D0 0%,#DDD6FE 55%,#FED7AA 100%)",
            }}>
              {isOwner && (
                <button className="absolute right-3 bottom-3 inline-flex items-center gap-1.5 rounded-full bg-white/85 backdrop-blur ring-1 ring-slate-200 hover:ring-violet-400 text-xs font-semibold text-slate-700 px-3 py-1.5" data-testid="sp-profile-edit-cover">
                  <CameraIcon className="w-3.5 h-3.5" /> Edit cover
                </button>
              )}
            </div>
            <div className="px-5 sm:px-8 pb-6 relative">
              <div className="flex items-end -mt-14 sm:-mt-16 gap-4 flex-wrap">
                <div className="relative shrink-0">
                  <span className="block w-28 h-28 sm:w-32 sm:h-32 rounded-full ring-4 ring-white overflow-hidden bg-slate-200 shadow-md">
                    {id.avatar_url && <img src={id.avatar_url} alt="" className="w-full h-full object-cover" />}
                  </span>
                  {isOwner && (
                    <button className="absolute bottom-1 right-1 w-8 h-8 grid place-items-center rounded-full bg-violet-600 text-white ring-2 ring-white shadow-sm hover:bg-violet-700" data-testid="sp-profile-edit-avatar">
                      <CameraIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="flex-1 min-w-0 pt-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="font-serif font-bold text-slate-900 text-2xl sm:text-3xl leading-tight" data-testid="sp-profile-name">
                      {id.name || `@${id.handle}`}
                    </h1>
                    {id.verified && <BadgeCheck className="w-5 h-5 text-blue-500" />}
                  </div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <span className="text-slate-500 text-sm" data-testid="sp-profile-handle">@{id.handle}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 ring-1 ring-emerald-200 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active
                    </span>
                  </div>
                  {(id.city || id.country) && (
                    <p className="mt-1.5 text-sm text-slate-600 inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" /> {[id.city, id.country].filter(Boolean).join(", ")}
                    </p>
                  )}
                  <p className="mt-1 text-sm">
                    <a href={`https://${shareUrl}`} className="text-violet-700 hover:text-violet-900 font-semibold inline-flex items-center gap-1">
                      <Link2 className="w-3.5 h-3.5" /> {shareUrl}
                    </a>
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 pt-2 shrink-0" data-testid="sp-profile-actions">
                  {isOwner ? (
                    <>
                      <Link to="/profile/edit" data-testid="sp-profile-edit-btn" className="inline-flex items-center gap-1.5 rounded-full ring-1 ring-slate-300 hover:ring-violet-400 text-slate-700 hover:text-violet-700 font-semibold px-4 py-2 text-sm transition-colors">Edit Profile</Link>
                      <button onClick={() => navigator.clipboard?.writeText(`https://${shareUrl}`)} data-testid="sp-profile-share-btn" className="inline-flex items-center gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-4 py-2 text-sm shadow-sm">
                        Share Profile
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="inline-flex items-center gap-1.5 rounded-full ring-1 ring-slate-300 hover:ring-violet-400 text-slate-700 hover:text-violet-700 font-semibold px-4 py-2 text-sm">Message</button>
                      <button className="inline-flex items-center gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-4 py-2 text-sm shadow-sm">Follow</button>
                    </>
                  )}
                </div>
              </div>

              {id.bio && (
                <p className="mt-5 text-sm sm:text-[15px] text-slate-700 leading-relaxed max-w-3xl" data-testid="sp-profile-bio">
                  {id.bio}
                </p>
              )}

              <div className="mt-5 grid grid-cols-4 gap-2 rounded-xl bg-slate-50 p-3" data-testid="sp-profile-stats">
                <Stat n={stats.followers} label="Followers" />
                <Stat n={stats.following} label="Following" />
                <Stat n={stats.posts} label="Posts" />
                <Stat n={stats.socialCap} label="Social Capital" accent />
              </div>
            </div>
          </section>

          {/* Composer */}
          {isOwner && (
            <section className="mt-4 rounded-2xl bg-white ring-1 ring-slate-200 p-4" data-testid="sp-profile-composer">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full overflow-hidden bg-slate-200 shrink-0">
                  {id.avatar_url && <img src={id.avatar_url} alt="" className="w-full h-full object-cover" />}
                </span>
                <button className="flex-1 h-10 px-4 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 text-sm text-left transition-colors" data-testid="sp-profile-composer-open">
                  What's on your mind today?
                </button>
              </div>
              <div className="mt-3 flex items-center gap-1 flex-wrap">
                <ComposerChip icon={Camera} label="Photo" tint="emerald" />
                <ComposerChip icon={FileText} label="Article" tint="violet" />
                <ComposerChip icon={Calendar} label="Event" tint="orange" />
                <ComposerChip icon={LineChart} label="Poll" tint="blue" />
                <ComposerChip icon={Plus} label="More" tint="slate" />
              </div>
            </section>
          )}

          {/* Feed placeholder */}
          <section className="mt-4">
            <div className="flex items-center justify-between mb-3 px-1">
              <h2 className="font-serif font-bold text-slate-900 text-lg">My Pulse</h2>
              <button className="text-sm font-semibold text-violet-700 hover:text-violet-900">View all</button>
            </div>
            <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-8 text-center text-sm text-slate-500" data-testid="sp-profile-feed-empty">
              <PlusCircle className="w-8 h-8 text-violet-300 mx-auto mb-3" />
              <p className="font-semibold text-slate-700">
                {isOwner ? "Your feed is quiet — share the first pulse" : `${id.name?.split(" ")[0] || "They"} hasn't posted yet.`}
              </p>
              <p className="mt-1 text-[13px] text-slate-500">
                Posts, photos, articles and events show up here.
              </p>
            </div>
          </section>
        </main>

        {/* ── Right rail ── */}
        <aside className="space-y-4" data-testid="sp-profile-right">
          {/* Social Capital */}
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-slate-800 text-sm">Social Capital</p>
              <Link to="/social-capital" className="text-xs font-semibold text-violet-700 hover:text-violet-900">View all</Link>
            </div>
            <p className="font-serif font-bold text-violet-700 text-3xl">
              {stats.socialCap.toLocaleString()}
              <Activity className="inline-block w-5 h-5 ml-2 -mt-1 text-violet-500" />
            </p>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mt-0.5">
              Total Points · {data.social_capital?.level || "Rookie"}
            </p>
            <MiniBars total={stats.socialCap} />
          </div>

          {/* Top Badges */}
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-slate-800 text-sm">Top Badges</p>
              <Link to="/badges" className="text-xs font-semibold text-violet-700 hover:text-violet-900">See all</Link>
            </div>
            <ul className="grid grid-cols-4 gap-2 text-center">
              {BADGES.map((b) => (
                <li key={b.key} className="flex flex-col items-center gap-1">
                  <span className={`w-11 h-11 grid place-items-center rounded-full ring-1 ${TINTS[b.tint]}`}>
                    <Award className="w-4 h-4" />
                  </span>
                  <p className="text-[10px] font-semibold text-slate-700 leading-tight">{b.label}</p>
                </li>
              ))}
            </ul>
          </div>

          {/* My Links */}
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-slate-800 text-sm">My Links</p>
              <Link to="/profile/edit#links" className="text-xs font-semibold text-violet-700 hover:text-violet-900">See all</Link>
            </div>
            <ul className="space-y-2" data-testid="sp-profile-links">
              {(data.links || []).slice(0, 5).map((l) => {
                const Icon = LINK_ICONS[l.icon] || Link2;
                return (
                  <li key={l.url}>
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-slate-50 transition-colors">
                      <span className="w-8 h-8 grid place-items-center rounded-full bg-slate-100 text-slate-700">
                        <Icon className="w-4 h-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">{l.label}</p>
                        <p className="text-[11px] text-slate-500 truncate">{prettyHost(l.url)}</p>
                      </div>
                      <ExternalLink className="w-3 h-3 text-slate-400" />
                    </a>
                  </li>
                );
              })}
              {(data.links || []).length === 0 && (
                <li className="text-xs text-slate-500 px-2 py-1">No links yet.</li>
              )}
            </ul>
            {isOwner && (
              <button className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold h-9">
                <Plus className="w-3.5 h-3.5" /> Add New Link
              </button>
            )}
          </div>

          {/* Marketplace */}
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-slate-800 text-sm">
                Marketplace <span className="text-slate-400 font-normal">({data.marketplace?.active_count || 0} Active)</span>
              </p>
              <Link to="/marketplace" className="text-xs font-semibold text-violet-700 hover:text-violet-900">See all</Link>
            </div>
            <ul className="space-y-2" data-testid="sp-profile-marketplace">
              {(data.marketplace?.items || []).slice(0, 4).map((it) => (
                <li key={it.id} className="flex items-center gap-2.5 rounded-lg px-1 py-1">
                  <span className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                    {(it.hero_image || (it.images && it.images[0])) && (
                      <img src={it.hero_image || it.images[0]} alt="" className="w-full h-full object-cover" />
                    )}
                  </span>
                  <p className="text-sm font-semibold text-slate-800 flex-1 min-w-0 truncate">{it.title}</p>
                  <span className="text-xs font-bold text-emerald-700 shrink-0">
                    {formatPrice(it.price, it.currency, it.listing_type)}
                  </span>
                </li>
              ))}
              {(data.marketplace?.items || []).length === 0 && (
                <li className="text-xs text-slate-500 px-2 py-1">No active listings.</li>
              )}
            </ul>
            {isOwner && (
              <Link to="/marketplace/new" className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold h-9">
                <Plus className="w-3.5 h-3.5" /> Post New Listing
              </Link>
            )}
          </div>

          {/* Events */}
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-slate-800 text-sm">Upcoming Events</p>
              <Link to="/events" className="text-xs font-semibold text-violet-700 hover:text-violet-900">See all</Link>
            </div>
            <ul className="space-y-3" data-testid="sp-profile-events">
              {(data.upcoming_events || []).slice(0, 3).map((e) => (
                <li key={e.id} className="flex items-start gap-2.5">
                  <Calendar className="w-4 h-4 text-orange-600 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{e.title}</p>
                    <p className="text-[11px] text-slate-500">
                      {formatEventDate(e.start_at || e.starts_at)} · {e.city || e.venue || "—"}
                    </p>
                  </div>
                </li>
              ))}
              {(data.upcoming_events || []).length === 0 && (
                <li className="text-xs text-slate-500 px-2 py-1">No upcoming events.</li>
              )}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ─────────────────── UI atoms ─────────────────── */

function IconBtn({ icon: Icon, label, href, tint }) {
  const cls = `w-9 h-9 grid place-items-center rounded-full ${tint ? "bg-violet-600 text-white hover:bg-violet-700" : "text-slate-600 hover:bg-slate-100"}`;
  return href ? (
    <Link to={href} aria-label={label} className={cls}><Icon className="w-4 h-4" /></Link>
  ) : (
    <button aria-label={label} className={cls}><Icon className="w-4 h-4" /></button>
  );
}

function SidebarItem({ label, icon: Icon, active, onClick, badge }) {
  return (
    <li>
      <button
        onClick={onClick}
        data-testid={`sp-profile-tab-${label.toLowerCase().replace(/\s+/g,"-")}`}
        className={`w-full flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors ${
          active ? "bg-violet-600 text-white" : "text-slate-700 hover:bg-slate-50"
        }`}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span className="flex-1 min-w-0 truncate text-sm font-semibold">{label}</span>
        {badge != null && badge > 0 && (
          <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${active ? "bg-white/25" : "bg-violet-100 text-violet-700"}`}>
            {badge}
          </span>
        )}
      </button>
    </li>
  );
}

function Stat({ n, label, accent }) {
  return (
    <div className="text-center">
      <p className={`font-serif font-bold text-lg sm:text-xl ${accent ? "text-violet-700" : "text-slate-800"}`}>
        {formatCount(n)}
      </p>
      <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 mt-0.5">
        {label}
      </p>
    </div>
  );
}

function ComposerChip({ icon: Icon, label, tint }) {
  return (
    <button className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors" data-testid={`sp-profile-compose-${label.toLowerCase()}`}>
      <Icon className={`w-3.5 h-3.5 ${
        tint === "emerald" ? "text-emerald-600" :
        tint === "violet"  ? "text-violet-600"  :
        tint === "orange"  ? "text-orange-600"  :
        tint === "blue"    ? "text-blue-600"    : "text-slate-500"
      }`} />
      {label}
    </button>
  );
}

function MiniBars({ total }) {
  // Deterministic little bar chart placeholder that fits the mockup —
  // proper history plot will read from `point_events` in a later pass.
  const bars = useMemo(() => {
    const heights = [];
    for (let i = 0; i < 14; i++) {
      const seed = ((total || 100) + i * 137) % 100;
      heights.push(20 + (seed % 80));
    }
    return heights;
  }, [total]);
  return (
    <div className="mt-3 flex items-end gap-[3px] h-10">
      {bars.map((h, i) => (
        <span
          key={i}
          className="flex-1 rounded-sm bg-violet-300/70"
          style={{ height: `${h}%` }}
        />
      ))}
    </div>
  );
}

/* ─────────────────── helpers ─────────────────── */

function countBadge(data, key) {
  if (key === "marketplace") return data.marketplace?.active_count || 0;
  if (key === "services")    return data.services?.active_count || 0;
  return 0;
}
function prettyHost(url) {
  try { return new URL(url).host.replace(/^www\./, ""); } catch { return url; }
}
function formatCount(n) {
  if (n == null) return "0";
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  return String(n);
}
function formatPrice(price, currency, kind) {
  if (kind === "free" || (price === 0 && kind !== "auction")) return "Free";
  if (price == null) return "";
  const c = currency === "USD" ? "$" : (currency || "$");
  return `${c}${Number(price).toLocaleString()}`;
}
function formatEventDate(iso) {
  if (!iso) return "TBD";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
}
function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}
